import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { Observable, throwError } from 'rxjs'
import * as AuthActions from '../+state/auth.actions'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src'
import { catchError, filter, switchMap, take } from 'rxjs/operators'

/**
 * Interceptor which handles attaching an auth header to HTTP requests
 * for authenticated users, and refreshing the token as necessary.
 */
@Injectable()
export class AuthInterceptor<T> implements HttpInterceptor {
  // Add <T> here
  isRefreshing = false // flag to prevent multiple refresh token requests

  constructor(
    @Inject(APP_CONFIG) protected appConfig: any,
    private authFacade: AuthFacade,
    private gbFrontCookieService: GbFrontCookieService,
  ) {}

  intercept(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    if (!req.withCredentials) {
      return next.handle(req)
    }

    const token = this.gbFrontCookieService.getTokenCookie()
    const modifiedReq = req.clone()

    if (!token || !modifiedReq.url.includes(this.appConfig.apiUrl) || !modifiedReq.withCredentials) {
      return next.handle(modifiedReq)
    }

    return this.handleRequest(modifiedReq, next, token)
  }

  private handleRequest(modifiedReq: HttpRequest<T>, next: HttpHandler, token?: string): Observable<HttpEvent<T>> {
    const requestWithAuth = modifiedReq.clone({
      headers: modifiedReq.headers.append('Authorization', `Token ${token}`),
    })
    return next.handle(requestWithAuth).pipe(catchError((error) => this.handleError(error, modifiedReq, next, token)))
  }

  private handleError(error: HttpErrorResponse, modifiedReq: HttpRequest<T>, next: HttpHandler, prevToken?: string) {
    if (error.status === 401) {
      if (this.isRefreshUrl(modifiedReq.url)) {
        this.isRefreshing = false
        return throwError(() => error) // logout handled by refreshTokenFailure effect
      }

      if (!this.isRefreshing) {
        this.isRefreshing = true
        this.authFacade.dispatch(AuthActions.refreshToken())
      }

      return this.authFacade.token$.pipe(
        filter((token) => !!token && token !== prevToken),
        take(1),
        switchMap((token) => {
          this.isRefreshing = false
          return this.handleRequest(modifiedReq, next, token)
        }),
      )
    }

    return throwError(() => error)
  }

  private isRefreshUrl(url: string): boolean {
    return url.endsWith(`/login/${this.appConfig.refreshTokenUrl}/`)
  }
}
